import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "react-toastify";
import { Copy, TrendingUp, Users, Link, Search, Eye } from "lucide-react";
import { API } from "./adminConfig";

const ReferralsTab = () => {
  const [referralData, setReferralData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      // Get all users with their referral stats
      const usersResponse = await axios.get(`${API}/admin/users`);
      const users = usersResponse.data;
      
      const referralPromises = users.map(async (user) => {
        try {
          const discountResponse = await axios.get(`${API}/subscription/referral-discount/${user.id}`);
          return {
            ...user,
            referral_stats: discountResponse.data
          };
        } catch (error) {
          return {
            ...user,
            referral_stats: {
              active_referrals: 0,
              expired_referrals: 0,
              discount_percentage: 0,
              monthly_savings: 0,
              active_discounts: []
            }
          };
        }
      });
      
      const referralData = await Promise.all(referralPromises);
      // Filter to show only users with referral activity
      const activeReferrers = referralData.filter(user => 
        user.referral_stats.active_referrals > 0 || user.referral_stats.expired_referrals > 0
      );
      
      setReferralData(activeReferrers);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Error loading referral data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDaysRemaining = (days) => {
    if (days <= 0) return 'Expired';
    if (days <= 30) return `${days} days left`;
    if (days <= 90) return `${Math.round(days/7)} weeks left`;
    return `${Math.round(days/30)} months left`;
  };

  const openUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy mx-auto"></div>
        <span className="mt-2 text-burgundy">Loading referral data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Referral Tracking</h2>
        <Button onClick={fetchReferralData} className="bg-burgundy hover:bg-burgundy/80 text-cream">
          <Search className="h-4 w-4 mr-2" />
          Refresh Data
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-burgundy/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-burgundy">
              {referralData.reduce((sum, user) => sum + user.referral_stats.active_referrals, 0)}
            </div>
            <div className="text-sm text-burgundy/60">Active Referral Discounts</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-burgundy/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-burgundy">
              {referralData.reduce((sum, user) => sum + user.referral_stats.expired_referrals, 0)}
            </div>
            <div className="text-sm text-burgundy/60">Expired Discounts</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-burgundy/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-burgundy">
              ${referralData.reduce((sum, user) => sum + user.referral_stats.monthly_savings, 0).toFixed(2)}
            </div>
            <div className="text-sm text-burgundy/60">Total Monthly Savings</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-burgundy/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-burgundy">{referralData.length}</div>
            <div className="text-sm text-burgundy/60">Active Referrers</div>
          </CardContent>
        </Card>
      </div>

      {/* Referrers List */}
      <div className="space-y-4">
        {referralData.length === 0 ? (
          <Card className="bg-white border-burgundy/20">
            <CardContent className="text-center py-8">
              <Users className="h-16 w-16 mx-auto mb-4 opacity-30 text-burgundy" />
              <p className="text-burgundy/60">No active referrers found.</p>
            </CardContent>
          </Card>
        ) : (
          referralData.map((user) => (
            <Card key={user.id} className="bg-white border-burgundy/20">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-burgundy">{user.name}</h3>
                      <span className="text-sm text-burgundy/60">({user.email})</span>
                      {user.referral_stats.active_referrals > 0 && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          {user.referral_stats.discount_percentage}% off
                        </span>
                      )}
                    </div>
                    
                    <div className="grid md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-burgundy/60">Active Referrals</div>
                        <div className="font-semibold text-burgundy">{user.referral_stats.active_referrals}</div>
                      </div>
                      <div>
                        <div className="text-burgundy/60">Expired</div>
                        <div className="font-semibold text-burgundy">{user.referral_stats.expired_referrals}</div>
                      </div>
                      <div>
                        <div className="text-burgundy/60">Monthly Savings</div>
                        <div className="font-semibold text-green-600">${user.referral_stats.monthly_savings}</div>
                      </div>
                      <div>
                        <div className="text-burgundy/60">Current Price</div>
                        <div className="font-semibold text-burgundy">${user.referral_stats.current_monthly_price}</div>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openUserDetails(user)}
                    className="text-burgundy border-burgundy hover:bg-burgundy/10"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <Card className="w-full max-w-2xl bg-white border-burgundy/20">
            <CardHeader>
              <CardTitle className="font-serif text-burgundy">
                {selectedUser.name}'s Referral Details
              </CardTitle>
              <CardDescription>{selectedUser.email}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-cream/20 p-4 rounded-lg">
                    <div className="text-sm text-burgundy/60 mb-1">Current Discount</div>
                    <div className="text-2xl font-bold text-burgundy">
                      {selectedUser.referral_stats.discount_percentage}%
                    </div>
                    <div className="text-sm text-green-600">
                      Saves ${selectedUser.referral_stats.monthly_savings}/month
                    </div>
                  </div>
                  <div className="bg-cream/20 p-4 rounded-lg">
                    <div className="text-sm text-burgundy/60 mb-1">Next Benefit</div>
                    <div className="text-sm text-burgundy">
                      {selectedUser.referral_stats.next_referral_benefit}
                    </div>
                  </div>
                </div>

                {selectedUser.referral_stats.active_discounts.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-burgundy mb-3">Active Discounts (Expiring)</h4>
                    <div className="space-y-2">
                      {selectedUser.referral_stats.active_discounts.map((discount, index) => (
                        <div key={index} className="flex justify-between items-center bg-green-50 border border-green-200 p-3 rounded-md">
                          <div>
                            <div className="font-medium text-green-700">
                              Referred: {discount.referred_email}
                            </div>
                            <div className="text-sm text-green-600">
                              Successful: {formatDate(discount.successful_date)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-green-700">10% off</div>
                            <div className="text-sm text-green-600">
                              {formatDaysRemaining(discount.days_remaining)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    onClick={() => setShowUserDetails(false)}
                    className="bg-burgundy hover:bg-burgundy/80 text-cream"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Enhanced Support Requests Tab Component with Management Features

export default ReferralsTab;
